import './stacks-temp.css';

import classNames from 'classnames';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import ReactPlayer from 'react-player';

import { useMediaQuery } from '../../hooks/useMediaQuery';
import { imageUrlFor } from '../../utils/image-url';
import { BannerVariant } from '../banner-variant';
import { Checklist } from '../checklist';
import { CostEstimator } from '../cost-estimator/cost-estimator';
import { CtaContainer } from '../ctaContainer';
import { FaqBlock } from '../faqBlock';
import { HostedVideo } from '../hosted-video';
import { IconList } from '../icon-list';
import { Iterable } from '../iterable';
import { LongformContent } from '../longform-content';
import { Lottie } from '../lottie';
import { MarketoForm } from '../marketo-form';
import { ProviderProfiles } from '../provider-profiles';
import { QuoteModule } from '../quote';
import { SimpleRoiCalculator } from '../simple-roi-calculator';
import { SingleImageWithSquiggle } from '../single-image-with-squiggle';
import { StacksCardCarousel } from '../stacks-card-carousel';
import { StacksProviderSearch } from '../stacks-provider-search';
import { StatesDropdownContainer } from '../states-dropdown';
import { WistiaEmbed } from '../wistia';
import * as styles from './stacks.module.css';

interface GradientStop {
	color: string;
	position: number;
}

interface Gradient {
	gradientType: 'linear' | 'radial';
	gradientDirection: string;
	colorStops: GradientStop[];
}

interface Content {
	_key: string;
	_type: string;
	customThumbnail?: {
		asset: any;
	};
	url?: string;
}

interface StacksProps {
	backgroundColor: string;
	backgroundGradient: Gradient;
	backgroundImage: any;
	backgroundStyle: string;
	bgImageAlignment: string;
	columnRatio: string;
	combinedBackgroundColor: string;
	fillStacksToFullHeight: boolean;
	longformDynamicContent: any;
	mobileBackgroundImage: any;
	reverseDirectionOnMobile: boolean;
	singleColumnRatio: string;
	stackName: string;
	stacks: any;
	stickyColumn: boolean;
	topPaddingOverride: any;
	twoColumnRatio: string;
	verticalCenter: boolean;
	verticalPadding: any;
}

export function Stacks({
	backgroundColor,
	backgroundGradient,
	backgroundImage,
	backgroundStyle,
	bgImageAlignment,
	columnRatio,
	combinedBackgroundColor,
	fillStacksToFullHeight,
	longformDynamicContent,
	mobileBackgroundImage,
	reverseDirectionOnMobile,
	singleColumnRatio,
	stackName,
	stacks,
	stickyColumn,
	topPaddingOverride,
	twoColumnRatio,
	verticalCenter,
	verticalPadding,
}: StacksProps) {
	// hooks
	const isMobile = useMediaQuery('(max-width: 799px)');
	const isTablet = useMediaQuery(
		'(min-width: 800px) and (max-width: 1279px)'
	);
	const isDesktop = useMediaQuery('(min-width: 1280px)');

	// content padding
	const modulePaddingTop = verticalPadding?.modulePaddingTop;
	const modulePaddingBottom = verticalPadding?.modulePaddingBottom;
	const topPaddingOverrideEnabled =
		topPaddingOverride?.topPaddingOverrideEnabled;
	const topPaddingOverrideStyle = {
		paddingTop: isMobile
			? `${topPaddingOverride?.topPaddingOverrideMobile}px`
			: isTablet
				? `${topPaddingOverride?.topPaddingOverrideTablet}px`
				: isDesktop
					? `${topPaddingOverride?.topPaddingOverrideDesktop}px`
					: null,
	};

	// bg image
	const bgImage = getImage(backgroundImage?.asset);
	const mobileBgImage = getImage(mobileBackgroundImage?.asset);

	// bg gradient
	const gradientType = backgroundGradient?.gradientType || 'linear'; // Default to 'linear'
	const gradientDirection =
		backgroundGradient?.gradientDirection || 'to right'; // Default direction
	const colorStops = backgroundGradient?.colorStops || []; // Array of { color, position }

	// Generate gradient style string
	const gradientStyle =
		gradientType === 'linear'
			? `linear-gradient(${gradientDirection}, ${colorStops
					.map((stop) => `${stop.color} ${stop.position}%`)
					.join(', ')})`
			: gradientType === 'radial'
				? `radial-gradient(circle, ${colorStops
						.map((stop) => `${stop.color} ${stop.position}%`)
						.join(', ')})`
				: null;

	// stack length
	const stackLength = stacks?.length;

	function refineStackName(stackName: string | undefined) {
		if (stackName) {
			return stackName
				.toLowerCase() // Convert to lowercase
				.replace(/[^a-z0-9-_]/g, '-') // Replace non-url-friendly characters with a hyphen
				.replace(/--+/g, '-') // Replace multiple hyphens with a single hyphen
				.replace(/^-+|-+$/g, ''); // Remove hyphens from the start and end
		} else {
			return ''; // You can customize this fallback value
		}
	}

	return (
		<div
			{...(stackName && { id: refineStackName(stackName) })}
			className={classNames(
				backgroundStyle === 'color' &&
					backgroundColor &&
					`b${backgroundColor}`,
				styles.moduleContainer,
				modulePaddingBottom,
				modulePaddingTop
			)}
			style={
				backgroundStyle === 'gradient' && gradientStyle
					? { background: gradientStyle }
					: {}
			}
		>
			{backgroundStyle === 'image' && backgroundImage && (
				<div
					className={classNames(
						'bg-image',
						styles.imageContainer,
						bgImageAlignment && styles[bgImageAlignment]
					)}
				>
					{isMobile && mobileBgImage ? (
						<GatsbyImage
							alt={mobileBgImage?.alt || ''}
							className={styles.image}
							image={mobileBgImage}
						/>
					) : bgImage ? (
						<GatsbyImage
							alt={bgImage?.alt || ''}
							className={styles.image}
							image={bgImage}
						/>
					) : null}
				</div>
			)}
			<div
				className={classNames(
					reverseDirectionOnMobile && styles.reverseDirectionOnMobile,
					stackLength === 2 && `${styles[columnRatio]}`,
					stackLength === 1 && styles[singleColumnRatio],
					stackLength === 2
						? twoColumnRatio
							? styles[twoColumnRatio]
							: styles.twoSpanTwelveColumns
						: '',
					styles.stacksContainer
				)}
				style={
					topPaddingOverrideEnabled ? topPaddingOverrideStyle : null
				}
			>
				{twoColumnRatio && combinedBackgroundColor && (
					<div
						className={classNames(
							styles.gapColor,
							combinedBackgroundColor &&
								`b${combinedBackgroundColor}`
						)}
					/>
				)}
				{stacks?.map((stack, index) => {
					const {
						_key,
						backgroundColor,
						fadeEffects,
						horizontalAlignment,
						cardStackPadding, //stack vertical padding
						hideOnMobile,
						stackGap,
						textColor,
						verticalAlignment,
						videoHeight,
					} = stack;

					// content padding
					const cardStackPaddingHorizontal =
						cardStackPadding?.paddingHorizontal;
					const cardStackPaddingVertical =
						cardStackPadding?.paddingVertical;

					// fade effects
					const enableFadeEffect = fadeEffects?.enableFadeEffect;
					const fadeEffectType = fadeEffects?.fadeEffectType;
					const fadeEffectDirection =
						fadeEffects?.fadeEffectDirection;

					//height when a hosted video is included
					const hostedVideoHeight = {
						height: isMobile
							? `${videoHeight?.heightMobile}px`
							: isTablet
								? `${videoHeight?.heightTablet}px`
								: isDesktop
									? `${videoHeight?.heightDesktop}px`
									: 'auto',
					};

					return (
						<div
							key={_key}
							className={classNames(
								backgroundColor && `b${backgroundColor}`,
								fadeEffectType && fadeEffectType,
								fillStacksToFullHeight &&
									styles.fillStacksToFullHeight,
								stickyColumn && styles.stickyColumn,
								verticalCenter && styles.selfVertical,
								hideOnMobile && styles.hideOnMobile,
								cardStackPaddingHorizontal,
								cardStackPaddingVertical,
								styles.stackContainer,
								styles[`stackIndex${index}`],
								textColor,
								'stacks-each'
							)}
						>
							<div
								className={classNames(
									enableFadeEffect && 'reveal',
									fadeEffectDirection && fadeEffectDirection,
									stackGap && stackGap,
									styles.contentContainer,
									styles[horizontalAlignment],
									styles[verticalAlignment]
								)}
							>
								{stack?.content?.map((content: Content) => {
									const { _key, _type } = content;

									switch (_type) {
										case 'longformContent':
											return (
												<LongformContent
													className={
														styles.portableText
													}
													dynamicContent={
														longformDynamicContent
													}
													key={_key}
													{...content}
												/>
											);
										case 'bannerVariant':
											return (
												<BannerVariant
													key={_key}
													{...content}
												/>
											);
										case 'ctaContainer':
											return (
												<CtaContainer
													key={_key}
													{...content}
												/>
											);
										case 'singleImageWithSquiggle':
											return (
												<SingleImageWithSquiggle
													key={_key}
													{...content}
												/>
											);
										case 'wistiaEmbed':
											return (
												<WistiaEmbed
													key={_key}
													{...content}
												/>
											);
										case 'videoEmbed':
											return (
												<ReactPlayer
													className={`react-player ${styles.reactPlayer}`}
													controls
													width='100%'
													light={
														content.customThumbnail
															?.asset
															? imageUrlFor(
																	content
																		.customThumbnail
																		.asset
																).url() ??
																undefined
															: undefined
													}
													height='auto'
													playing={true}
													key={_key}
													url={content.url}
												/>
											);
										case 'videoHosted':
											return (
												<HostedVideo
													key={_key}
													videoHeightStyle={
														hostedVideoHeight
													}
													{...content}
												/>
											);
										case 'checklist':
											return (
												<Checklist
													key={_key}
													{...content}
												/>
											);
										case 'faq':
											return (
												<FaqBlock
													key={_key}
													{...content}
												/>
											);
										case 'iterableContainer':
											return (
												<Iterable
													key={_key}
													{...content}
												/>
											);
										case 'marketoContainer':
											return (
												<MarketoForm
													key={_key}
													{...content}
												/>
											);
										case 'quoteText':
											return (
												<QuoteModule
													key={_key}
													{...content}
												/>
											);
										case 'statesDropdownContainer':
											return (
												<StatesDropdownContainer
													key={_key}
													{...content}
												/>
											);
										case 'lottieAnimation':
											return (
												<Lottie
													key={_key}
													{...content}
												/>
											);
										case 'iconList':
											return (
												<IconList
													key={_key}
													{...content}
												/>
											);
										case 'stacksProviderSearch':
											return (
												<StacksProviderSearch
													{...content}
													key={_key}
												/>
											);
										case 'stacksCardCarousel':
											return (
												<StacksCardCarousel
													key={_key}
													{...content}
												/>
											);
										case 'simpleRoiCalculator':
											return (
												<SimpleRoiCalculator
													key={_key}
													{...content}
												/>
											);
										case 'providerProfilesSearch':
											return (
												<ProviderProfiles
													{...content}
													key={_key}
												/>
											);
										case 'costEstimator':
											return (
												<CostEstimator
													key={_key}
													{...content}
												/>
											);
										default:
											return null;
									}
								})}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
